<template>
  <LoadingOverlay v-if="isLoading" />
  <div v-else>
    <b-container :fluid="true">
      <b-row>
        <b-col cols="9">
          <h1>Members</h1>
        </b-col>
        <b-col class="center">
          <button
            class="custom-button blue rounded uk-margin-remove"
            @click="showAddMemberModal"
          >
            Add Members
          </button>
        </b-col>
      </b-row>
    </b-container>
    <hr>
    <b-card>
      <b-card-body>
        <b-row>
          <h3><strong>Actions:</strong></h3>
        </b-row>
        <b-row>
          <h4 v-if="!selectedMembers.length">
            Select one or more members
          </h4>
          <h4 v-else>
            {{ selectedMessage }}
          </h4>
        </b-row>
        <b-row
          v-if="selectedMembers.length"
          fluid
          class="flex-row"
        >
          <b-button
            size="sm"
            variant="outline-danger"
            :disabled="removePending"
            @click="removeMembers"
          >
            <b-spinner v-if="removePending">
              Loading
            </b-spinner>
            <span v-else>Remove Selected</span>
          </b-button>
          <span
            v-if="isApprovedAvailable"
            class="p-2"
          />
          <b-button
            v-if="isApprovedAvailable"
            size="sm"
            variant="outline-success"
            :disabled="confirmPending"
            @click="approveMembers"
          >
            <b-spinner v-if="confirmPending">
              Loading
            </b-spinner>
            <span v-else>Approve Selected</span>
          </b-button>
        </b-row>
      </b-card-body>
    </b-card>
    <b-table
      ref="membersTable"
      striped
      hover
      :selectable="!actionPending"
      responsive="sm"
      sticky-header="400px"
      select-mode="multi"
      primary-key="id"
      :fields="fields"
      :items="filtered"
      class="align-middle"
      show-empty
      @row-selected="onRowSelected"
    >
      <template #head(selected)>
        <span>&nbsp;&nbsp;&nbsp;</span>
      </template>

      <template #cell(authorized)="data">
        <span>{{ data.item.authorized === 1 ? 'yes': 'no' }}</span>
      </template>

      <template #emptyfiltered="scope">
        <h4>{{ scope.emptyFilteredText }}</h4>
      </template>

      <template #top-row="{fields: filterFields}">
        <td
          v-for="field in filterFields"
          :key="field.key"
        >
          <label>
            <input
              v-if="Object.keys(filters).includes(field.key)"
              v-model="filters[field.key]"
              :placeholder="field.label"
            >
          </label>
        </td>
      </template>

      <template #cell(selected)="{ rowSelected, index }">
        <template>
          <b-container
            :fluid="true"
            class="d-flex flex-fill center"
            style="height: 100%"
          >
            <b-form-checkbox
              :checked="rowSelected"
              @change="(checked)=>rowCheckboxClicked(checked,index)"
            />
            <span
              v-if="rowSelected"
              class="sr-only"
            >Selected</span>
            <span
              v-else
              class="sr-only"
            >Not selected</span>
          </b-container>
        </template>
      </template>
    </b-table>
    <ModalOverlay
      id="addMemberModal"
      ref="addMemberModal"
      modal-id="addMemberModal"
      :modal-show="showAddMember"
      :title="`Add New Members`"
      size="lg"
      ok-title="Add"
      @confirmed="submitAddMember"
      @hide="addMemberModalClosed"
    >
      <b-tabs content-class="mt-3">
        <b-tab
          title="Add Member"
          active
        >
          <b-form
            v-if="showAddMember"
            @submit.prevent="submitAddMember"
          >
            <b-form-group
              id="memberEmail"
              label="Member Email"
              label-for="memberEmailInput"
              :invalid-feedback="addMemberError"
              :state="addMemberHasError"
              label-sr-only
            >
              <b-form-input
                id="memberEmailInput"
                v-model="newMember.email"
                type="text"
                required
                placeholder="Enter member email"
                :state="addMemberHasError"
              />
            </b-form-group>
            <b-form-group
              id="memberName"
              label="Member Name"
              label-for="memberNameInput"
              label-sr-only
            >
              <b-form-input
                id="memberNameInput"
                v-model="newMember.name"
                type="text"
                required
                placeholder="Enter member name"
              />
            </b-form-group>
          </b-form>
        </b-tab>
        <b-tab title="Upload user list">
          <h4>
            Add Multiple Members: -
            <a href="https://org-blocit-production-laravel.s3.us-east-2.amazonaws.com/Blocit-Bulk-Member-Form.xlsx">
              Click to download multi-member file template
            </a>
          </h4>
          <vue-dropzone
            id="dropzone"
            ref="myVueDropzone"
            :options="dropzoneOptions"
            @submit.prevent=""
            @vdropzone-files-added="fileAdded"
          />
        </b-tab>
      </b-tabs>
    </ModalOverlay>
  </div>
</template>

<script>
import axios from 'axios';
import get from 'lodash/get';
import LoadingOverlay from '../../common/LoadingOverlay';
import { emitToast, VARIANT_TYPE } from '../../../util/Toast';
import { validate } from '../../../util';
import ModalOverlay from '../../common/ModalOverlay';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import XLSX from 'xlsx';
import { isEmpty } from 'lodash';
import { mapState } from 'vuex';

export default {
  name: 'Members',
  components: {
    LoadingOverlay,
    ModalOverlay,
    vueDropzone: vue2Dropzone,
  },
  data: () => ({
    actionPending:false,
    addMemberError: '',
    confirmPending:false,
    dropzoneOptions: {
      autoQueue: false,
      maxFiles: 1,
      maxFilesize: 1.5,
      thumbnailWidth:150,
      url: '#',
    },
    emailRule: { email: true, required: true, stop: true },
    emptyMessage: 'Loading Members...',
    fields: [
      { key: 'selected', label: '&nbsp;', sortable: false },
      { key: 'name', label: 'Name', sortable: true },
      { key: 'email', sortable: true },
      { key: 'authorized', label: 'Approved', sortable: true },
    ],
    fileUploaded: false,
    filters: {
      authorized: '',
      email: '',
      name: '',
    },
    isApprovedAvailable: false,
    isLoading: true,
    members: '',
    membersToBeAdded: [],
    membershipTypeRule: { format: /^[A-Za-z\s]+$/, required: true, stop: true },
    nameRule: { alpha: true, required: true, stop: true },
    newMember: {},
    pendingMembers: 0,
    removePending:false,
    selectedConfirmed: false,
    selectedMembers: [],
    showAddMember: false,
  }), //End methods
  computed: {
    ...mapState({
      organization: state => state.organization,
    }),
    addMemberHasError() {
      return this.addMemberError.length > 0 ? false :  undefined;
    },

    filtered() {
      const filtered = this.members.filter(item => {
        return Object.keys(this.filters).every(key => {
          if (key === 'authorized'){
            return String(get(item, key) === 1 ? 'yes' : 'no').includes(this.filters[key].toLowerCase());
          } else {
            return String(get(item, key).toLowerCase()).includes(this.filters[key].toLowerCase());
          }
        });
      });
      return filtered.length > 0 ? filtered : [];
    },

    selectedMessage() {
      const selectedReduce = this.selectedMembers.reduce((map, obj) => {
        const status = obj.authorized === 1 ? 'approved':'pending';
        map[status] = map[status]?[obj].concat(map[status]):[obj];
        return map;
      }, {});

      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.isApprovedAvailable = selectedReduce.pending && !selectedReduce.approved;

      const plural = this.selectedMembers.length > 1;
      if (this.selectedMembers.length <= 0)
        return 'Select one or more members';
      else
        return `There ${plural ? 'are' : 'is'} currently ${this.selectedMembers.length}
          member${plural ? 's' : ''} selected  (
          ${(selectedReduce.pending? `${selectedReduce.pending.length} pending` : '') +
          (selectedReduce.approved?
            `${selectedReduce.pending?', ': ''}${selectedReduce.approved.length} approved` : '')})`;
    },
  },
  created() {
    this.getMembers();
  },
  methods: {

    addMemberModalClosed() {
      if (this.membersToBeAdded.length === 0 && isEmpty(this.newMember)){
        this.addMemberError = '';
      }

      this.fileUploaded = false;
    },

    async approveMembers(){
      this.confirmPending = this.actionPending = true;
      try {
        await axios.post(
          `/api/${this.organization.slug}/manage/member/approve`,
          { members: this.selectedMembers }
        );
        for (const user of this.selectedMembers) {
          const userToUpdate = this.members.find(obj => obj.id === user.id);
          userToUpdate.authorized = 1;
        }
        emitToast(this.$bvToast, VARIANT_TYPE.SUCCESS, 'Selected users have been approved!');
        this.selectedMembers = [];
      } catch ( e ) {
        // Do nothing
      } finally {
        this.confirmPending = this.actionPending = false;
      }

    },

    async fileAdded(files) {
      const vueDropzone = this.$refs.myVueDropzone;
      const buffer = await files[0].arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const users = XLSX.utils.sheet_to_json(worksheet);
      const newMembers = [];
      const misconfiguredUsers = [];
      for (const [i, user] of users.entries()) {
        const {
          'First Name': firstName,
          'Last Name': lastName,
          'Email': email,
          'Membership Type': membershipType,
        } = user;

        try {
          validate(firstName, this.nameRule);
          validate(lastName, this.nameRule);
          validate(email, this.emailRule);
          validate(membershipType, this.membershipTypeRule);
          newMembers.push({ email, firstName, lastName, membershipType });
        } catch (e){
          misconfiguredUsers.push({ index:i });
        }

        this.$refs.myVueDropzone.getAcceptedFiles()[0]
          .previewElement.querySelector('.dz-upload')
          .style.width = ((i+1)/users.length) * 100;
      }

      this.fileUploaded = true;

      if (misconfiguredUsers.length){
        vueDropzone.dropzone.emit('complete', vueDropzone.getAddedFiles()[0]);
        vueDropzone.dropzone.emit('error',
          vueDropzone.getAddedFiles()[0],
          'An error occurred, please verify you entered valid values'
        );
        emitToast(this.$bvToast, VARIANT_TYPE.DANGER,
          'An error occurred while reading the user spreadsheet, please verify you entered valid values'
        );
      } else {
        vueDropzone. dropzone.emit('complete', vueDropzone.getAddedFiles()[0]);
        vueDropzone.dropzone.emit('success', vueDropzone.getAddedFiles()[0]);
        this.membersToBeAdded = newMembers;
      }
    },

    async getMembers() {
      const response = await axios.get(`/api/${this.organization.slug}/manage/member`);
      this.members = response.data;
      this.emptyMessage = 'There are currently no members';
      this.isLoading = false;
      this.getPendingMembersCount();
    },

    async getPendingMembersCount() {
      let pendingCount = 0;
      for (const member of this.members){
        if (member.authorized === 0) pendingCount ++;
      }

      this.pendingMembers = pendingCount;
      if (pendingCount) {
        const plural = pendingCount > 1;
        emitToast(this.$bvToast, VARIANT_TYPE.WARNING,
          `There ${plural ? 'are' : 'is'} currently ${pendingCount} member${plural ? 's' : ''} awaiting approval.`
        );
      }
    },
    onRowSelected(selected) {
      this.selectedMembers = selected;
    },

    async removeMembers() {
      this.removePending = this.actionPending = true;
      try {
        await axios.post(
          `/api/${this.organization.slug}/manage/member/remove`,
          { members: this.selectedMembers }
        );
        const idsToRemove = this.selectedMembers.map(member => member.id);
        this.members = this.members.filter(member => !idsToRemove.includes(member.id));
        emitToast(this.$bvToast, VARIANT_TYPE.SUCCESS, 'Selected users have been removed!');
        this.selectedMembers = [];
      } catch ( e ) {
        // Do nothing
      } finally {
        this.removePending = this.actionPending = false;
      }
    },

    rowCheckboxClicked(checked,index){
      if (checked) {
        this.$refs.membersTable.selectRow(index);
      } else {
        this.$refs.membersTable.unselectRow(index);
      }
    },

    showAddMemberModal() {
      this.showAddMember = true;
      this.$refs.addMemberModal.show();
    },

    async submitAddMember(e) {
      e.preventDefault();
      const membersToAdd = [];
      // members added via file upload
      if (this.fileUploaded){
        if (this.membersToBeAdded && this.membersToBeAdded.length) {
          membersToAdd.push(...this.membersToBeAdded);
        }
      } else {
        try {
          validate(this.newMember.email,this.emailRule);
        } catch ( err ){
          this.addMemberError = 'Invalid email entered';
          emitToast(this.$bvToast, VARIANT_TYPE.WARNING,
            'An error occurred when adding members. Please try again.'
          );
          return false;
        }
        const name = this.newMember.name.split(' ');
        const newMember = {
          email: this.newMember.email,
          firstName: name[0],
          lastName: name[1] || '',
          membershipType: 'new member',
        };
        membersToAdd.push(newMember);
      }
      try {
        if (membersToAdd.length) {
          const { data: { addedMembers } } = await axios.post(
            `/api/${ this.organization.slug }/manage/member`,
            { newMembers: membersToAdd }
          );
          this.members.push(...addedMembers);
          this.membersToBeAdded = 0;
          this.newMember = {};
          emitToast(this.$bvToast, VARIANT_TYPE.SUCCESS, 'New members added!');
        }
      } catch ({ response }){
        emitToast(this.$bvToast, VARIANT_TYPE.WARNING,'An error occurred when adding members. Please try again.');
      } finally {
        this.$refs.addMemberModal.hide();
      }

    },
  }, // End computed
};
</script>

<style scoped>
    table td {
        vertical-align: middle !important;
    }

    #dropzone {
      height: 250px;
    }
</style>
